import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import Pagination from '../components/Pagination';
import GlobalDateFilter from '../components/GlobalDateFilter';
import usePagination from '../hooks/usePagination';
import {
  MdSearch, MdFileDownload, MdRefresh, MdArrowUpward, MdArrowDownward,
  MdClose, MdArticle, MdCalendarToday, MdBarChart, MdOpenInNew, MdLocationCity,
  MdFilterList, MdContentCopy, MdCheck
} from 'react-icons/md';
import { getArticleWebsiteUrl } from '../utils/format';

/* ─────────────── helpers ─────────────── */
const CITY_TELUGU_MAP = {
  'all_india': 'జాతీయం',
  'All India': 'జాతీయం',
  'all india': 'జాతీయం',
  'ALL INDIA': 'జాతీయం',
  'ఆల్ ఇండీయా': 'జాతీయం',
  'ఆల్ ఇండియా': 'జాతీయం',
  'అల్ ఇండియా': 'జాతీయం',
  'జాతీయం': 'జాతీయం',
  'international': 'అంతర్జాతీయం',
  'International': 'అంతర్జాతీయం',
  'అంతర్జాతీయ': 'అంతర్జాతీయం',
  'అంతర్జాతీయం': 'అంతర్జాతీయం',
};

function formatCityTelugu(city) {
  if (!city || city === '—' || city.trim() === '') return '—';
  const s = city.trim();
  return CITY_TELUGU_MAP[s] || CITY_TELUGU_MAP[s.toLowerCase()] || s;
}

const STATE_TELUGU_MAP = {
  'Telangana': 'తెలంగాణ',
  'Andhra Pradesh': 'ఆంధ్రప్రదేశ్',
  'andhra pradesh': 'ఆంధ్రప్రదేశ్',
  'AndhraPradesh': 'ఆంధ్రప్రదేశ్',
  'AP': 'ఆంధ్రప్రదేశ్',
  'ap': 'ఆంధ్రప్రదేశ్',
  'All India': 'జాతీయం',
  'all_india': 'జాతీయం',
  'all india': 'జాతీయం',
  'ALL INDIA': 'జాతీయం',
  'ఆల్ ఇండియా': 'జాతీయం',
  'అల్ ఇండియా': 'జాతీయం',
  'జాతీయం': 'జాతీయం',
  'International': 'అంతర్జాతీయం',
  'international': 'అంతర్జాతీయం',
  'అంతర్జాతీయ': 'అంతర్జాతీయం',
  'అంతర్జాతీయం': 'అంతర్జాతీయం',
  'Karnataka': 'కర్ణాటక',
  'Tamil Nadu': 'తమిళనాడు',
  'Maharashtra': 'మహారాష్ట్ర',
  'Delhi': 'ఢిల్లీ',
  'Kerala': 'కేరళ',
  'Odisha': 'ఒడిశా',
};

function formatStateTelugu(state) {
  if (!state || state === '—' || state.trim() === '') return '—';
  const s = state.trim();
  return STATE_TELUGU_MAP[s] || STATE_TELUGU_MAP[s.toLowerCase()] || s;
}

function downloadCSV(rows, filename) {
  const header = ['City', 'State', "Posts Count"];
  const lines  = [header, ...rows.map(r => [formatCityTelugu(r.city), formatStateTelugu(r.state), r.todayPosts])];
  const csv    = lines.map(l => l.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob   = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url    = URL.createObjectURL(blob);
  const a      = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function getFilterTitleSuffix(filter, fromDate, toDate) {
  switch (filter) {
    case 'today':
      return 'Today';
    case 'yesterday':
      return 'Yesterday';
    case 'last7':
    case 'last7days':
      return 'Last 7 Days';
    case 'last30':
    case 'last30days':
      return 'Last 30 Days';
    case 'thisMonth':
      return 'This Month';
    case 'lastMonth':
      return 'Last Month';
    case 'custom':
      if (fromDate && toDate) {
        const formatStr = (dStr) => {
          const d = new Date(dStr + 'T00:00:00');
          return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        };
        return `Custom Range (${formatStr(fromDate)} – ${formatStr(toDate)})`;
      }
      return 'Custom Range';
    default:
      return 'Today';
  }
}

function getFilterSubtitle(filter, reportDate, fromDate, toDate) {
  if (filter === 'today' || filter === 'yesterday') {
    const d = filter === 'yesterday'
      ? new Date(Date.now() - 86400000)
      : (reportDate ? new Date(reportDate + 'T00:00:00') : new Date());
    return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  }
  if (filter === 'custom' && fromDate && toDate) {
    return `${fromDate} to ${toDate}`;
  }
  return `Filtered by ${getFilterTitleSuffix(filter, fromDate, toDate)}`;
}

/* ─────────────── page ─────────────── */
export default function CityReport() {
  /* report state */
  const [report,     setReport]     = useState([]);
  const [totalToday, setTotalToday] = useState(0);
  const [reportDate, setReportDate] = useState('');
  const [allStates,  setAllStates]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  /* date filter state */
  const [dateFilter,  setDateFilter]  = useState('today');
  const [customRange, setCustomRange] = useState({ from: '', to: '' });

  /* search / filter / sort / pagination */
  const [search,      setSearch]      = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [sortKey,     setSortKey]     = useState('todayPosts');   // 'city' | 'state' | 'todayPosts'
  const [sortDir,     setSortDir]     = useState('desc');
  const { page, limit: PAGE_SIZE, setPage, setLimit } = usePagination(15);

  /* drill-down modal */
  const [drillModal,   setDrillModal]   = useState(null);   // { city }
  const [drillData,    setDrillData]    = useState([]);
  const [drillPage,    setDrillPage]    = useState(1);
  const [drillLimit,   setDrillLimit]   = useState(10);
  const [drillPaging,  setDrillPaging]  = useState({ page: 1, pages: 1, total: 0 });
  const [drillLoading, setDrillLoading] = useState(false);
  const [copiedId,     setCopiedId]     = useState(null);

  const refreshTimer = useRef(null);

  /* ── fetch report ── */
  const fetchReport = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const params = { filter: dateFilter };
      if (dateFilter === 'custom' && customRange.from && customRange.to) {
        params.from_date = customRange.from;
        params.to_date   = customRange.to;
        params.startDate = customRange.from;
        params.endDate   = customRange.to;
      }
      const res = await api.get('/admin/reports/city-today', { params });
      setReport(res.data.data || []);
      setTotalToday(res.data.totalPosts ?? res.data.totalToday ?? 0);
      setReportDate(res.data.date || '');
      setAllStates(res.data.states || []);
    } catch {
      toast.error('Failed to load city report');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateFilter, customRange.from, customRange.to]);

  useEffect(() => {
    fetchReport();
    refreshTimer.current = setInterval(() => fetchReport(true), 60_000);
    return () => clearInterval(refreshTimer.current);
  }, [fetchReport]);

  /* ── drill-down fetch ── */
  const fetchDrill = useCallback(async (city, p = 1, l = drillLimit) => {
    setDrillLoading(true);
    try {
      const params = { city, page: p, limit: l, filter: dateFilter };
      if (dateFilter === 'custom' && customRange.from && customRange.to) {
        params.from_date = customRange.from;
        params.to_date   = customRange.to;
        params.startDate = customRange.from;
        params.endDate   = customRange.to;
      }
      const res = await api.get('/admin/reports/city-today/articles', { params });
      setDrillData(res.data.data || []);
      setDrillPaging(res.data.pagination || { page: 1, pages: 1, total: 0 });
      setDrillPage(p);
    } catch {
      toast.error('Failed to load articles');
    } finally {
      setDrillLoading(false);
    }
  }, [dateFilter, customRange.from, customRange.to, drillLimit]);

  const handleDrillLimitChange = (newLimit) => {
    setDrillLimit(newLimit);
    if (drillModal) fetchDrill(drillModal.city, 1, newLimit);
  };

  const openDrill = (city) => {
    setDrillModal({ city });
    setDrillData([]);
    setDrillPage(1);
    fetchDrill(city, 1);
  };

  /* ── copy public article link ── */
  const handleCopyLink = async (article) => {
    try {
      const url = getArticleWebsiteUrl(article);
      if (!url) {
        throw new Error('No article URL available');
      }

      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (!successful) throw new Error('execCommand failed');
      }

      setCopiedId(article.id);
      setTimeout(() => {
        setCopiedId((curr) => (curr === article.id ? null : curr));
      }, 2000);

      toast.success('Link copied successfully');
    } catch (err) {
      console.error('Failed to copy link:', err);
      toast.error('Unable to copy link. Please try again.');
    }
  };

  /* ── sorting ── */
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'todayPosts' ? 'desc' : 'asc'); }
    setPage(1);
  };

  /* ── derived data ── */
  const uniqueStates = Array.from(
    new Set(allStates.map(s => formatStateTelugu(s)).filter(s => s && s !== '—'))
  ).sort((a, b) => a.localeCompare(b, 'te'));

  const filtered = report.filter(r => {
    const normCity  = formatCityTelugu(r.city);
    const normState = formatStateTelugu(r.state);
    const matchCity  = normCity.toLowerCase().includes(search.toLowerCase()) || r.city.toLowerCase().includes(search.toLowerCase());
    const matchState = !stateFilter || normState === stateFilter || r.state === stateFilter;
    return matchCity && matchState;
  });

  const sorted = [...filtered].sort((a, b) => {
    let va = a[sortKey], vb = b[sortKey];
    if (typeof va === 'string') va = va.toLowerCase();
    if (typeof vb === 'string') vb = vb.toLowerCase();
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ?  1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE) || 1;
  const paginated  = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const maxPosts   = Math.max(...report.map(r => r.todayPosts), 1);

  const filterTitleSuffix = getFilterTitleSuffix(dateFilter, customRange.from, customRange.to);
  const filterSubtitle    = getFilterSubtitle(dateFilter, reportDate, customRange.from, customRange.to);

  const mostActiveCity = report.length > 0
    ? report.reduce((best, r) => r.todayPosts > best.todayPosts ? r : best, report[0])
    : null;

  /* ─────────────── render ─────────────── */
  if (loading) return <div className="page-loader">Loading report…</div>;

  return (
    <div className="page cr-page" style={{ opacity: refreshing ? 0.7 : 1, transition: 'opacity 0.2s' }}>

      {/* ── header ── */}
      <div className="page-header cr-header">
        <div>
          <h2 className="cr-title">City Report — {filterTitleSuffix}</h2>
          <p className="cr-subtitle">
            <MdCalendarToday style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {filterSubtitle}
          </p>
        </div>
        <div className="cr-header-actions">
          <GlobalDateFilter
            filter={dateFilter}
            fromDate={customRange.from}
            toDate={customRange.to}
            onChange={(f, from, to) => {
              setDateFilter(f);
              setCustomRange({ from, to });
              setPage(1);
            }}
          />
          <button
            className="cr-btn cr-btn-ghost"
            onClick={() => fetchReport(true)}
            disabled={refreshing}
            title="Refresh report"
          >
            <MdRefresh className={refreshing ? 'cr-spin' : ''} />
            Refresh
          </button>
          <button
            className="cr-btn cr-btn-primary"
            onClick={() => downloadCSV(sorted, `city-report-${dateFilter}-${reportDate || 'date'}.csv`)}
          >
            <MdFileDownload />
            Export CSV
          </button>
        </div>
      </div>

      {/* ── summary cards ── */}
      <div className="cr-summary-row">
        <div className="cr-card cr-card-highlight">
          <div className="cr-card-icon"><MdArticle /></div>
          <div>
            <div className="cr-card-value">{totalToday}</div>
            <div className="cr-card-label">Total Posts ({filterTitleSuffix})</div>
          </div>
        </div>
        <div className="cr-card">
          <div className="cr-card-icon cr-icon-blue"><MdLocationCity /></div>
          <div>
            <div className="cr-card-value">{report.filter(r => r.todayPosts > 0).length}</div>
            <div className="cr-card-label">Active Cities</div>
          </div>
        </div>
        <div className="cr-card">
          <div className="cr-card-icon cr-icon-gray"><MdBarChart /></div>
          <div>
            <div className="cr-card-value">{report.filter(r => r.todayPosts === 0).length}</div>
            <div className="cr-card-label">Silent Cities</div>
          </div>
        </div>
        <div className="cr-card">
          <div className="cr-card-icon cr-icon-green"><MdLocationCity /></div>
          <div>
            <div className="cr-card-value" style={{ fontSize: (mostActiveCity?.city && formatCityTelugu(mostActiveCity.city).length > 10) ? '1.1rem' : undefined }}>
              {mostActiveCity ? formatCityTelugu(mostActiveCity.city) : '—'}
            </div>
            <div className="cr-card-label">Most Active City</div>
          </div>
        </div>
      </div>

      {/* ── toolbar ── */}
      <div className="cr-toolbar">
        {/* Search */}
        <div className="cr-search-wrap">
          <MdSearch className="cr-search-icon" />
          <input
            className="cr-search-input"
            placeholder="Search city…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button className="cr-search-clear" onClick={() => { setSearch(''); setPage(1); }}>
              <MdClose />
            </button>
          )}
        </div>

        {/* State filter */}
        <div className="cr-filter-wrap">
          <MdFilterList className="cr-filter-icon" />
          <select
            className="cr-filter-select"
            value={stateFilter}
            onChange={e => { setStateFilter(e.target.value); setPage(1); }}
          >
            <option value="">All States</option>
            {uniqueStates.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        <div className="cr-results-info">
          {filtered.length} cit{filtered.length === 1 ? 'y' : 'ies'} found
        </div>
      </div>

      {/* ── table ── */}
      <div className="cr-table-wrap">
        <table className="cr-table">
          <thead>
            <tr>
              <th className="cr-th cr-th-num">#</th>
              <th
                className={`cr-th cr-th-sortable ${sortKey === 'city' ? 'cr-th-active' : ''}`}
                onClick={() => toggleSort('city')}
              >
                City
                {sortKey === 'city'
                  ? (sortDir === 'asc' ? <MdArrowUpward className="cr-sort-icon" /> : <MdArrowDownward className="cr-sort-icon" />)
                  : <MdArrowUpward className="cr-sort-icon cr-sort-muted" />}
              </th>
              <th
                className={`cr-th cr-th-sortable ${sortKey === 'state' ? 'cr-th-active' : ''}`}
                onClick={() => toggleSort('state')}
              >
                State
                {sortKey === 'state'
                  ? (sortDir === 'asc' ? <MdArrowUpward className="cr-sort-icon" /> : <MdArrowDownward className="cr-sort-icon" />)
                  : <MdArrowUpward className="cr-sort-icon cr-sort-muted" />}
              </th>
              <th
                className={`cr-th cr-th-sortable cr-th-right ${sortKey === 'todayPosts' ? 'cr-th-active' : ''}`}
                onClick={() => toggleSort('todayPosts')}
              >
                {dateFilter === 'today' ? "Today's Posts" : "Posts Count"}
                {sortKey === 'todayPosts'
                  ? (sortDir === 'asc' ? <MdArrowUpward className="cr-sort-icon" /> : <MdArrowDownward className="cr-sort-icon" />)
                  : <MdArrowDownward className="cr-sort-icon cr-sort-muted" />}
              </th>
              <th className="cr-th">Progress</th>
              <th className="cr-th cr-th-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={6} className="cr-empty">No report data available for the selected date range.</td>
              </tr>
            ) : (
              paginated.map((row, idx) => {
                const pct  = maxPosts > 0 ? (row.todayPosts / maxPosts) * 100 : 0;
                const rank = (page - 1) * PAGE_SIZE + idx + 1;
                return (
                  <tr key={row.city} className={`cr-tr ${row.todayPosts > 0 ? 'cr-tr-active' : 'cr-tr-zero'}`}>
                    <td className="cr-td cr-td-num">{rank}</td>
                    <td className="cr-td">
                      <span className="cr-cat-name">{formatCityTelugu(row.city)}</span>
                    </td>
                    <td className="cr-td">
                      {row.state !== '—' ? (
                        <span className="cr-state-badge">{formatStateTelugu(row.state)}</span>
                      ) : (
                        <span className="cr-state-none">—</span>
                      )}
                    </td>
                    <td className="cr-td cr-td-right">
                      <span className={`cr-badge ${row.todayPosts > 0 ? 'cr-badge-active' : 'cr-badge-zero'}`}>
                        {row.todayPosts}
                      </span>
                    </td>
                    <td className="cr-td cr-td-bar">
                      <div className="cr-progress-bg">
                        <div
                          className="cr-progress-fill"
                          style={{ width: `${pct}%` }}
                          title={`${pct.toFixed(1)}% of max`}
                        />
                      </div>
                    </td>
                    <td className="cr-td cr-td-right">
                      <button
                        className="cr-drilldown-btn"
                        onClick={() => openDrill(row.city)}
                        disabled={row.todayPosts === 0}
                        title={row.todayPosts === 0 ? 'No articles' : `View ${row.todayPosts} articles`}
                      >
                        <MdOpenInNew />
                        View
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── pagination ── */}
      <Pagination
        pagination={{ page, pages: totalPages, total: filtered.length }}
        onPageChange={p => { setPage(p); window.scrollTo(0, 0); }}
        limit={PAGE_SIZE}
        onLimitChange={setLimit}
      />

      {/* ───────────── drill-down modal ───────────── */}
      {drillModal && (
        <div className="cr-modal-overlay" onClick={() => setDrillModal(null)}>
          <div className="cr-modal" onClick={e => e.stopPropagation()}>
            <div className="cr-modal-header">
              <div>
                <h3 className="cr-modal-title">{drillModal.city}</h3>
                <p className="cr-modal-sub">Published articles ({filterTitleSuffix})</p>
              </div>
              <button className="cr-modal-close" onClick={() => setDrillModal(null)}>
                <MdClose />
              </button>
            </div>

            {drillLoading ? (
              <div className="cr-modal-loader">Loading articles…</div>
            ) : drillData.length === 0 ? (
              <div className="cr-modal-empty">No published articles found for this period.</div>
            ) : (
              <>
                <div className="cr-modal-count">
                  {drillPaging.total} article{drillPaging.total !== 1 ? 's' : ''} in {filterTitleSuffix} in {drillModal.city}
                </div>
                <div className="cr-modal-list">
                  {drillData.map((article, i) => {
                    const isCopied = copiedId === article.id;
                    return (
                      <div key={article.id} className="cr-article-row">
                        <div className="cr-article-num">{(drillPage - 1) * drillLimit + i + 1}</div>
                        {article.image_url && (
                          <img
                            className="cr-article-thumb"
                            src={article.image_url}
                            alt=""
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <div className="cr-article-info">
                          <div className="cr-article-title">{article.title}</div>
                          <div className="cr-article-meta">
                            <span className="cr-meta-cat">{article.category}</span>
                            <span>·</span>
                            <span>{article.author_name}</span>
                            <span>·</span>
                            <span>{new Date(article.published_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                            <span>·</span>
                            <span>{article.view_count} views</span>
                            <span>·</span>
                            <span>{article.like_count} likes</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className={`cr-copy-btn ${isCopied ? 'copied' : ''}`}
                          onClick={() => handleCopyLink(article)}
                          title="Copy public article link"
                        >
                          {isCopied ? <MdCheck /> : <MdContentCopy />}
                          <span>{isCopied ? 'Copied' : 'Copy Link'}</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
                <Pagination
                  pagination={{ page: drillPage, pages: drillPaging.pages, total: drillPaging.total }}
                  onPageChange={p => fetchDrill(drillModal.city, p)}
                  limit={drillLimit}
                  onLimitChange={handleDrillLimitChange}
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
